import React from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from '@/utils/appStyleSheet';
import { FontFamilies } from '@/constants/Typography';
import useWindow from '@/hooks/useWindow';

export type ClaimStage = 'inQueue' | 'mailed' | 'received' | 'reviewing' | 'decision';

const STAGES: { key: ClaimStage; label: string }[] = [
  { key: 'inQueue', label: 'In queue' },
  { key: 'mailed', label: 'Mailed to\nadministrator' },
  { key: 'received', label: 'Received by\nadministrator' },
  { key: 'reviewing', label: 'Administrator\nreview' },
  { key: 'decision', label: 'Decision' },
];

type ClaimProgressBarProps = {
  currentStage: ClaimStage;
};

/**
 * Get the stage index (0-4) for a given stage key
 */
const getStageIndex = (stage: ClaimStage): number => {
  return STAGES.findIndex((s) => s.key === stage);
};

/**
 * Determine the current claim stage based on claim data
 *
 * Stages (checked in order):
 * 1. If no dueAt (orphaned claim with no suit), default to "decision"
 * 2. If mailedAt is null, use dueAt as mailedAt
 * 3. Check dueAt (claim window deadline):
 *    - Decision: 9 months after claim window closes
 *    - Reviewing: 1 month after claim window closes
 * 4. Check mailedAt for mailing-based stages:
 *    - Received: 10 days after mailing
 *    - Mailed: right after mailing
 */
export const getClaimStage = (data: {
  createdAt?: string;
  mailedAt?: string;
  dueAt?: string;
  claimed?: boolean;
}): ClaimStage => {
  const now = Date.now();

  // If no dueAt (orphaned claim with no suit), default to decision
  if (!data.dueAt) {
    return 'decision';
  }

  // If mailedAt is null, use dueAt as the mailing date
  const effectiveMailedAt = data.mailedAt || data.dueAt;

  // Check dueAt - if claim window has closed, use deadline-based stages
  const dueDate = new Date(data.dueAt).getTime();
  const monthsSinceDue = (now - dueDate) / (1000 * 60 * 60 * 24 * 30);

  // Decision: 9 months after claim window closes
  if (monthsSinceDue >= 9) {
    return 'decision';
  }

  // Reviewing: 1 month after claim window closes
  if (monthsSinceDue >= 1) {
    return 'reviewing';
  }

  // Check mailedAt for mailing-based stages
  const mailedDate = new Date(effectiveMailedAt).getTime();
  const daysSinceMailed = Math.floor((now - mailedDate) / (1000 * 60 * 60 * 24));

  // Received: 10 days after mailing
  if (daysSinceMailed >= 10) {
    return 'received';
  }

  // Mailed: right after mailing
  return 'mailed';
};

/**
 * Get status badge info based on claim stage
 */
export const getStatusBadge = (
  stage: ClaimStage
): { text: string; color: string; backgroundColor: string } => {
  switch (stage) {
    case 'inQueue':
      return {
        text: 'In queue',
        color: '#333333',
        backgroundColor: 'rgba(20, 20, 20, 0.04)',
      };
    case 'mailed':
      return {
        text: 'Mailed',
        color: '#A4160E',
        backgroundColor: '#FDE7D1',
      };
    case 'received':
      return {
        text: 'Received',
        color: '#0369A1',
        backgroundColor: '#E0F2FE',
      };
    case 'reviewing':
      return {
        text: 'Administrator review',
        color: '#7C3AED',
        backgroundColor: '#EDE9FE',
      };
    case 'decision':
      return {
        text: 'Decision',
        color: '#5E990A',
        backgroundColor: '#F8FFE0',
      };
  }
};

export function ClaimProgressBar({ currentStage }: ClaimProgressBarProps) {
  const currentIndex = getStageIndex(currentStage);
  const { isDesktop } = useWindow();

  return (
    <View style={styles.container}>
      {/* Progress track */}
      <View style={styles.trackContainer}>
        {/* Background track */}
        <View style={styles.backgroundTrack} />
        {/* Filled track */}
        <View
          style={[
            styles.filledTrack,
            { width: `${(currentIndex / (STAGES.length - 1)) * 100}%` },
          ]}
        />
        {/* Stage dots */}
        {STAGES.map((stage, index) => {
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          return (
            <View
              key={stage.key}
              style={[
                styles.dot,
                { left: `${(index / (STAGES.length - 1)) * 100}%` },
                isCompleted ? styles.dotCompleted : styles.dotPending,
                isCurrent && styles.dotCurrent,
              ]}
            />
          );
        })}
      </View>
      {/* Labels */}
      <View style={styles.labelsContainer}>
        {STAGES.map((stage, index) => {
          const isCompleted = index <= currentIndex;
          const isFirst = index === 0;
          const isLast = index === STAGES.length - 1;
          return (
            <View
              key={stage.key}
              style={[
                styles.labelWrapper,
                { left: `${(index / (STAGES.length - 1)) * 100}%` },
                isFirst && styles.labelWrapperFirst,
                isLast && styles.labelWrapperLast,
                stage.key === 'reviewing' && styles.labelWrapperReviewing,
              ]}
            >
              {stage.label.split('\n').map((line, lineIndex) => (
                <Text
                  key={lineIndex}
                  style={[
                    styles.label,
                    isCompleted ? styles.labelCompleted : styles.labelPending,
                    isDesktop && styles.labelDesktop,
                    stage.key === 'reviewing' && (isDesktop ? styles.labelReviewingDesktop : styles.labelReviewingMobile),
                  ]}
                  numberOfLines={1}
                >
                  {line}
                </Text>
              ))}
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: 0,
  },
  trackContainer: {
    height: 12,
    position: 'relative',
    justifyContent: 'center',
  },
  backgroundTrack: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: '#D9D9D9',
    borderRadius: 4,
  },
  filledTrack: {
    position: 'absolute',
    left: 0,
    height: 4,
    backgroundColor: '#0F9129',
    borderRadius: 4,
  },
  dot: {
    position: 'absolute',
    width: 12,
    height: 12,
    borderRadius: 6,
    marginLeft: -6,
    top: 0,
  },
  dotCompleted: {
    backgroundColor: '#0F9129',
  },
  dotPending: {
    backgroundColor: '#D9D9D9',
  },
  dotCurrent: {
    borderWidth: 2,
    borderColor: '#0F9129',
    backgroundColor: '#FFFFFF',
  },
  labelsContainer: {
    position: 'relative',
    height: 30,
    marginTop: 8,
  },
  labelWrapper: {
    position: 'absolute',
    alignItems: 'center',
    width: 70,
    marginLeft: -35,
  },
  labelWrapperFirst: {
    alignItems: 'flex-start',
    marginLeft: 0,
  },
  labelWrapperLast: {
    alignItems: 'flex-end',
    marginLeft: -70,
  },
  label: {
    fontSize: 9,
    lineHeight: 11,
    fontFamily: FontFamilies.InterTight.Regular,
    textAlign: 'center',
  },
  labelDesktop: {
    fontSize: 11,
    lineHeight: 14,
  },
  labelCompleted: {
    color: '#0F9129',
  },
  labelPending: {
    color: 'rgba(163, 163, 163, 0.7)',
  },
  labelReviewingMobile: {
    lineHeight: 10,
  },
  labelReviewingDesktop: {
    lineHeight: 12,
  },
  labelWrapperReviewing: {
    marginTop: 1,
  },
});
